<template>

    <Toast/>

    <MenuMain/>
    <MenuAdminOverview/>
    <HeadingOverviewDashboard/>

    <div class="px-6 surface-overlay relative">

        <div v-show="activeTab === 1" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Manage Companies</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callNewCompany" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>New Company</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <DataTable
                        v-model:lazyParams="lazyParams"
                        :value="companies"
                        lazy
                        paginator
                        :rows="10"
                        :totalRecords="totalRecords"
                        :loading="loading"
                        :rowsPerPageOptions="[10, 20, 50, 100]"
                        :sortMode="'multiple'"
                        :rowHover="true"
                        :striped-rows="true"
                        :reorderableColumns="true"
                        filterDisplay="menu"
                        responsiveLayout="scroll"
                        :globalFilterFields="['id', 'name', 'email', 'mobile']"
                        @page="onPage"
                        @sort="onSort"
                        @filter="onFilter">

                        <template #header>
                            <div class="p-grid">
                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedColumns" :options="columnsFullList"
                                                     optionLabel="header" @update:modelValue="onToggleColumns"
                                                     display="chip"
                                                     placeholder="Select Columns"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <Dropdown v-model="selectedStandardFilter" :options="statusTypes"
                                                  optionLabel="name" optionValue="code" placeholder="Select a Filter"
                                                  @update:modelValue="onToggleStandardFilters"/>
                                    </div>

                                </div>

                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-filter-slash" label="Clear Search"
                                                class="p-button-outlined p-ml-2" @click="clearFilter()"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-replay" label="Restore to Original View"
                                                class="p-button-outlined" @click="restoreToOriginal()"/>
                                    </div>
                                </div>

                                <div class="p-col-12 p-md-2">
                                    <span class="p-input-icon-left">
                                        <i class="pi pi-search"/>
                                        <InputText v-model="filters['global'].value"
                                                   placeholder="Keyword Search on ID, Name, Email or Mobile (Please press enter to search)"
                                                   style="width: 100%"
                                                   @keyup.enter="onSearch"/>
                                    </span>
                                </div>

                            </div>
                        </template>

                        <template #empty>
                            No companies found.
                        </template>

                        <template #loading>
                            Loading companies. Please wait.
                        </template>

                        <Column field="selectAnOption" style="width: 10%">
                            <template #body="{ data }">
                                <SplitButton icon="pi pi-pencil" label="Select" :model="getFilteredItems(data)"
                                             severity="success" @click="chooseBlankSelection(data)"
                                             class="mb-2 mr-2 ml-2 mt-2"/>
                            </template>
                        </Column>

                        <Column v-for="col in selectedColumns" :key="col.field" :field="col.field" :header="col.header"
                                :sortable="col.sortable">
                            <template v-if="col.field === 'id'" #body="{ data }">
                                <span class="p-column-title">ID</span>
                                {{ data.id }}
                            </template>

                            <template v-else-if="col.field === 'name'" #body="{ data }">
                                <span class="p-column-title">Name</span>
                                {{ data.name }}
                            </template>

                            <template v-else-if="col.field === 'type'" #body="{ data }">
                                <span class="p-column-title">Type</span>
                                <span v-if="data.type === 'S'" :class="'client-badge status-' + data.type">Sole Proprietor</span>
                                <span v-else-if="data.type === 'P'" :class="'client-badge status-' + data.type">Private Company</span>
                                <span v-else-if="data.type === 'N'" :class="'client-badge status-' + data.type">Non-Profit Company</span>
                                <span v-else-if="data.type === 'L'" :class="'client-badge status-' + data.type">Limited Company</span>
                                <span v-else-if="data.type === 'I'" :class="'client-badge status-' + data.type">Incorporation</span>
                                <span v-else-if="data.type === 'C'" :class="'client-badge status-' + data.type">Close Corporation</span>
                            </template>

                            <template v-else-if="col.field === 'status'" #body="{ data }">
                                <span class="p-column-title">Status</span>
                                <span v-if="data.status === 'I'"
                                      :class="'status-badge status-' + data.status">Inactive</span>
                                <span v-else-if="data.status === 'P'" :class="'status-badge status-' + data.status">Pending</span>
                                <span v-else-if="data.status === 'A'" :class="'status-badge status-' + data.status">Active</span>
                            </template>

                            <template v-else #body="{ data }">
                                {{ data[col.field] }}
                            </template>
                        </Column>
                    </DataTable>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 2" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Create Company</h3>

                    <div class="field">
                        <label for="productRole">Product Role?</label>
                        <Dropdown v-model="newCompany.productRole" :options="productRoleTypes" optionLabel="name" optionValue="code"
                                  placeholder="Select a Product Role"/>
                    </div>

                    <div class="field">
                        <label for="type">Type</label>
                        <Dropdown v-model="newCompany.type" :options="companyTypes" optionLabel="name" optionValue="code"
                                  placeholder="Select a Type"/>
                    </div>

                    <div class="field">
                        <label for="name">Name</label>
                        <InputText id="name" v-model.trim="newCompany.name" required="true"/>
                    </div>

                    <div class="field">
                        <label for="tradingName">Trading Name</label>
                        <InputText id="tradingName" v-model.trim="newCompany.tradingName" required="true"/>
                    </div>

                    <div class="field">
                        <label for="registrationNumber">Registration Number</label>
                        <InputText id="registrationNumber" v-model.trim="newCompany.registrationNumber" required="true"/>
                    </div>

                    <div class="field">
                        <label for="email">Email</label>
                        <InputText id="email" v-model.trim="newCompany.email" required="true"/>
                    </div>

                    <div class="field">
                        <label for="phone">Phone Number</label>
                        <InputText id="phone" v-model.trim="newCompany.phone" required="true"/>
                    </div>

                    <div class="field">
                        <label for="supportEmail">Support Email</label>
                        <InputText id="supportEmail" v-model.trim="newCompany.supportEmail" required="true"/>
                    </div>

                    <div class="field">
                        <label for="supportPhone">Support Phone Number</label>
                        <InputText id="supportPhone" v-model.trim="newCompany.supportPhone" required="true"/>
                    </div>

                    <div class="field">
                        <label for="assistant">Assistant?</label>
                        <Dropdown v-model="newCompany.assistant" :options="assistantTypes" optionLabel="name"
                                  optionValue="code" placeholder="Select an Option"/>
                    </div>

                    <div class="field">
                        <label for="taxRegistered">Tax Registered?</label>
                        <Dropdown v-model="newCompany.taxRegistered" :options="confirmationTypes" optionLabel="name"
                                  optionValue="code" placeholder="Select an Option"/>
                    </div>

                    <div v-show="newCompany.taxRegistered === 'Y'" class="field">
                        <label for="taxNumber">Tax Number</label>
                        <InputText id="taxNumber" v-model.trim="newCompany.taxNumber" required="false"/>
                    </div>

                    <div class="field">
                        <label for="addressCountry">Country</label>
                        <Dropdown v-model="newCompany.addressCountry" :options="countries" optionLabel="name"
                                  optionValue="code" placeholder="Select an Option"/>
                    </div>

                    <div class="field">
                        <label for="address1">Address Line 1</label>
                        <InputText id="address1" v-model.trim="newCompany.address1" required="true"/>
                    </div>

                    <div class="field">
                        <label for="address2">Address Line 2</label>
                        <InputText id="address2" v-model.trim="newCompany.address2" required="true"/>
                    </div>

                    <div class="field">
                        <label for="addressRegion">Region</label>
                        <InputText id="addressRegion" v-model.trim="newCompany.addressRegion" required="true"/>
                    </div>

                    <div v-show="newCompany.addressCountry === 'ZA'" class="field">
                        <label for="addressState">Province</label>
                        <Dropdown v-model="newCompany.addressState" :options="regionsSouthAfrica" optionLabel="name"
                                  optionValue="code" placeholder="Select an Option"/>
                    </div>

                    <div v-show="newCompany.addressCountry === 'UK'" class="field">
                        <label for="addressState">County</label>
                        <InputText id="addressState" v-model.trim="newCompany.addressState" required="true"/>
                    </div>

                    <div v-show="newCompany.addressCountry != 'ZA' && newCompany.addressCountry != 'UK'" class="field">
                        <label for="addressState">State</label>
                        <InputText id="addressState" v-model.trim="newCompany.addressState" required="true"/>
                    </div>

                    <div class="field">
                        <label for="addressPostalCode">Postal Code</label>
                        <InputText id="addressPostalCode" v-model.trim="newCompany.addressPostalCode" required="true"/>
                    </div>

                    <div class="field">
                        <label for="firstName">User First Name</label>
                        <InputText id="firstName" v-model.trim="newCompany.firstName" required="true"/>
                    </div>

                    <div class="field">
                        <label for="lastName">User Last Name</label>
                        <InputText id="lastName" v-model.trim="newCompany.lastName" required="true"/>
                    </div>

                    <div class="field">
                        <label for="userPassword">User Password</label>
                        <InputText id="userPassword" v-model.trim="newCompany.userPassword" required="true"/>
                    </div>

                    <Button label="Create" class="mr-2 mb-2" :loading="submitted" @click="createCompany"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callReset"></Button>

                </div>
            </div>
        </div>

    </div>

    <IsoEvolveFooter/>

</template>

<script>
import IsoEvolveFooter from "@/components/heading/authentication/IsoEvolveFooter.vue";
import MenuMain from "@/components/menu/common/MenuMain.vue";
import MenuAdminOverview from "@/components/menu/common/MenuAdminOverview.vue";
import HeadingOverviewDashboard from "@/components/heading/common/HeadingOverviewHome.vue";
import {useUserStore} from "@/stores/UserStore";
import UserService from "@/service/UserService";
import numeral from "numeral";
import CompanyService from "@/service/CompanyService";
import {FilterMatchMode, FilterOperator} from "primevue/api";

export default {
    components: {
        MenuMain, MenuAdminOverview,
        IsoEvolveFooter, HeadingOverviewDashboard
    },
    setup() {
        const userStore = useUserStore()

        return {
            userStore
        }
    },
    created() {
        this.initFilters();

        this.columnsDefault = [
            {field: 'name', header: 'Name', sortable: true},
            {field: 'type', header: 'Type', sortable: true},
            {field: 'email', header: 'Email', sortable: true},
            {field: 'phone', header: 'Phone', sortable: true},
            {field: 'status', header: 'Status', sortable: true}
        ];

        this.columnsFullList = [
            {field: 'id', header: 'ID', sortable: true},
            {field: 'name', header: 'Name', sortable: true},
            {field: 'type', header: 'Type', sortable: true},
            {field: 'email', header: 'Email', sortable: true},
            {field: 'phone', header: 'Phone', sortable: true},
            {field: 'status', header: 'Status', sortable: true}
        ];

        this.selectedColumns = this.columnsDefault;

        this.standardFilters = [
            {field: 'active', header: 'Active Companies'},
            {field: 'inactive', header: 'Inactive Companies'}
        ];
    },
    async mounted() {
        try {
            const userPingResponse = await UserService.getUserPing();
            console.log("[PmOverviewDashboard][mounted] System is active.");
            console.log("[PmOverviewDashboard][mounted] Data = ", userPingResponse);
        } catch (e) {
            console.log("[PmOverviewDashboard][mounted] System is NOT active.");
            console.log("[PmOverviewDashboard][mounted] Error = ", e);
            this.userStore.clearUser();
            this.$router.push("login");
            return;
        }

        if (!this.userStore.user.id) {
            console.log("[PmOverviewDashboard][mounted] User is NOT defined.");
            this.userStore.clearUser();
            this.$router.push('login');
            return;
        }

        try {
            if (!this.userStore.user) {
                const userData = await UserService.getUser(this.userStore.user.id);
                this.profile = userData.data;

                console.log("[Clients][mounted] userData = ", userData);
            }

            this.profile = this.userStore.user;

            console.log("[Clients][mounted] this.profile = ", this.profile);

            const userConfigurationData = await UserService.getUserConfig(this.userStore.user.id);
            this.userConfiguration = userConfigurationData.data;

            console.log("[Clients][mounted] this.userConfiguration = ", this.userConfiguration);

            // if (!this.userStore.companyConfiguration) {
            //     const companyConfigurationData = await CompanyService.getCompanyConfiguration(this.profile.companyId);
            //     this.userStore.setCompanyConfiguration(companyConfigurationData.data);
            // }
            //
            // this.companyConfiguration = this.userStore.companyConfiguration;

            const companyData = await CompanyService.getCompany(this.profile.companyId);
            this.company = companyData.data;

            console.log("[Clients][mounted] this.company = ", this.company);

            const companyConfigurationData = await CompanyService.getCompanyConfiguration(this.profile.companyId);
            this.userStore.setCompanyConfiguration(companyConfigurationData.data);
            this.companyConfiguration = companyConfigurationData.data;

            // const clientsData = await ClientService.getBasicCompanyClients(this.profile.companyId);
            // this.userStore.setClients(clientsData.data);
            // this.clients = clientsData.data;
            // this.originalClients = clientsData.data;

            this.loadData();

            // if (!this.userStore.clients) {
            //     const clientsData = await ClientService.getBasicCompanyClients(this.profile.companyId);
            //     this.userStore.setClients(clientsData.data);
            //     this.clients = clientsData.data;
            //     this.originalClients = clientsData.data;
            //
            //     console.log("[Clients][mounted] No clients in userStore. Refreshing data. this.clients = ", this.clients);
            // } else {
            //     this.clients = this.userStore.clients;
            //     this.originalClients = this.userStore.clients;
            //
            //     console.log("[Clients][mounted] Found clients in userStore. this.clients = ", this.clients);
            // }

            this.loading = false;

        } catch (e) {
            console.log("[PmOverviewDashboard][mounted] No user data found.");
            console.log("[PmOverviewDashboard][mounted] Error = ", e);
            this.userStore.clearUser();
            this.$router.push('login');
        }
    },
    data() {

        return {
            profile: {},
            financialSummary: {},
            auditTrails: null,
            totalRecords: 0,
            loading: false,
            lazyParams: {
                first: 0,
                rows: 10,
                sortField: null,
                sortOrder: null,
                filters: {}
            },
            userConfiguration: {},
            activeTab: 1,
            filters: null,
            inputFormat: null,
            file: null, // Holds the selected file
            url: "/client1/import/1/upload/document/evexia", // URL for API call
            fileAccept: 'application/pdf', // Accept CSV and PDF files
            inputFormats: [
                { name: 'Evexia (PDF)', code: '001' },
                { name: 'Scanned Hospital Form (PDF)', code: '002' }
            ],
            bulkSmsMessage: null,
            selectedColumns: null,
            columnsDefault: null,
            columnsFullList: null,
            selectedStandardFilter: null,
            standardFilters: null,
            useTaxRate: 'N',
            filteredItemsCache: new Map(),
            filteredItemsNotesCache: new Map(),
            submitted: false,
            reloadKey: 0,
            specificCompany: {},
            companies: null,
            newCompany: {},
            products: null,
            product: {},
            selectedProduct: {},
            clientProducts: null,
            notes: null,
            note: {},
            tasks: null,
            task: {},
            newTask: {},
            newStatement: {},
            invoice: {},
            unpaid: {},
            auditTrail: {},
            email: {},
            sms: {},
            company: {},
            companyConfiguration: {},
            clientConfiguration: {},
            filteredDiagnosticCodes: [],
            filteredProducts: [],
            filteredBillingPlans: [],
            filteredProcesses: [],
            filteredUsers: [],
            filteredCompanies: [],
            filteredAffiliateClients: [],
            filteredProviders: [],
            confirmationResponse: 'N',
            noteTemplates: null,
            noteTemplateId: null,
            statusTypes: [
                {name: 'Active', code: 'A'},
                {name: 'Inactive', code: 'I'},
                {name: 'Pending', code: 'P'}
            ],
            confirmationTypes: [
                {name: 'Yes', code: 'Y'},
                {name: 'No', code: 'N'}
            ],
            assistantTypes: [
                {name: 'None', code: 'N'},
                {name: 'Digital', code: 'D'},
                {name: 'Human', code: 'H'}
            ],
            clientTypes: [
                {name: 'Natural Person', code: 'N'},
                {name: 'Juristic Person', code: 'J'},
            ],
            companyTypes: [
                {name: 'Sole Proprietor', code: 'S'},
                {name: 'Private Company', code: 'P'},
                {name: 'Non-Profit Company', code: 'N'},
                {name: 'Limited Company', code: 'L'},
                {name: 'Incorporation', code: 'I'},
                {name: 'Close Corporation', code: 'C'}
            ],
            productRoleTypes: [
                {name: 'PM Module', code: 'P'},
                {name: 'MS Module', code: 'M'},
                {name: 'MouldingHealth Listing', code: 'C'},
                {name: 'SB Module', code: 'S'}
            ],
            noteTypes: [
                {name: 'Personal', code: 'P'},
                {name: 'Clinical', code: 'C'},
            ],
            formatTypes: [
                {name: 'Rich Text', code: 'R'},
                {name: 'Freeform', code: 'F'},
            ],
            subProcessTypes: [
                {name: 'Unpaids Management', code: 'Unpaids Management'},
                {name: 'Client Management', code: 'Client Management'},
                {name: 'Billing Management', code: 'Billing Management'}
            ],
            unpaidTypes: [
                {name: 'Member Liable', code: 'M'},
                {name: 'Gentle Notice', code: 'G'},
                {name: 'First Notice', code: 'F'},
                {name: 'Final Notice', code: 'S'},
                {name: 'Litigation Notice', code: 'L'},
                {name: 'Account Internally Written Off', code: 'C'}
            ],
            genderTypes: [
                {name: 'Male', code: 'M'},
                {name: 'Female', code: 'F'},
                {name: 'Other', code: 'O'}
            ],
            countries: [
                {name: 'United Kingdom', code: 'UK'},
                {name: 'South Africa', code: 'ZA'},
                {name: 'India', code: 'IN'},
                {name: 'United States', code: 'US'},
                {name: 'Namibia', code: 'NA'},
                {name: 'Australia', code: 'AU'},
                {name: 'New Zealand', code: 'NZ'},
                {name: 'Brazil', code: 'BR'},
                {name: 'China', code: 'CN'},
                {name: 'Egypt', code: 'EG'},
                {name: 'France', code: 'FR'},
                {name: 'Germany', code: 'DE'},
                {name: 'Japan', code: 'JP'},
                {name: 'Spain', code: 'ES'},
                {name: 'Slovenia', code: 'SV'},
            ],
            regionsSouthAfrica: [
                {name: 'Gauteng', code: 'Gauteng'},
                {name: 'Western Cape', code: 'Western Cape'},
                {name: 'Kwa-Zulu Natal', code: 'Kwa-Zulu Natal'},
                {name: 'North-West', code: 'North-West'},
                {name: 'Free State', code: 'Free State'},
                {name: 'Eastern Cape', code: 'Eastern Cape'},
                {name: 'Mpumalanga', code: 'Mpumalanga'},
                {name: 'Northern Cape', code: 'Northern Cape'},
                {name: 'Limpopo', code: 'Limpopo'}
            ],
            taskTypes: [
                {name: 'Software Support', code: 'S'},
                {name: 'Billing', code: 'B'},
                {name: 'Scheduling', code: 'C'},
                {name: 'Unpaids Management', code: 'U'},
                {name: 'Payment Management', code: 'P'},
                {name: 'Other', code: 'O'}
            ],
            taskTypesFinancialAdvisor: [
                {name: 'Administration', code: 'D'},
                {name: 'New Business', code: 'E'},
                {name: 'Benefit Query', code: 'F'},
                {name: 'Authorisation', code: 'G'},
                {name: 'Claims', code: 'H'},
                {name: 'Software Support', code: 'S'},
                {name: 'Billing', code: 'B'},
                {name: 'Scheduling', code: 'C'},
                {name: 'Unpaids Management', code: 'U'},
                {name: 'Payment Management', code: 'P'},
                {name: 'Other', code: 'O'}
            ],
        }
    },
    methods: {
        formatAmount(value) {
            if (value) {
                return numeral(String(value)).format('0,0.00');
            } else {
                return 0;
            }
        },
        initFilters() {
            this.filters = {
                'global': {value: null, matchMode: FilterMatchMode.CONTAINS},
                'id': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'name': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'email': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'mobile': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                }

            }
        },
        callReset() {
            this.activeTab = 1;
        },
        onToggleColumns(value) {
            this.selectedColumns = this.columnsFullList.filter(col =>
                value.some(selected => selected.field === col.field)
            );
        },
        getFilteredItems(data) {
            if (this.filteredItemsCache.has(data.id)) {
                return this.filteredItemsCache.get(data.id);
            }

            const items = [];

            items.push({
                label: 'Manage Users',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callManageUsers(data);
                }
            });

            items.push({
                label: 'Allocate Prepaid',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callAllocatePrepaid(data);
                }
            });

            // Store the computed items in the cache
            this.filteredItemsCache.set(data.id, items);

            return items;
        },
        chooseBlankSelection(data) {
            console.log('[chooseBlankSelection] data = ', data);
            this.$toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Please select a valid option.',
                life: 3000
            });
        },
        async loadData() {
            this.loading = true;
            try {
                console.log("[Clients][loadData] Fetching clients with params:", this.lazyParams, "Search:", this.filters['global']?.value);

                const companiesData = await CompanyService.getCompanies();
                this.companies = companiesData.data;
                console.log("[Clients][loadData] API Response:", companiesData.data);
                // console.log("[Clients][loadData] response.data.content:", response.data?.content);
                // console.log("[Clients][loadData] response.data.totalElements:", response.data?.totalElements);

                // this.clients = Array.isArray(response.data.content) ? response.data.content : [];
                // this.totalRecords = response.data?.totalElements || 0;
                //
                // console.log("[Clients][loadData] Clients Updated:", this.clients, "Total Records:", this.totalRecords);
            } catch (error) {
                console.error("[Clients][loadData] Error loading clients:", error);
                this.companies = [];
                this.totalRecords = 0;
            }
            this.loading = false;
        },
        onPage(event) {
            console.log("[Clients][onPage] Page Event:", event);
            this.lazyParams.first = event.first;
            this.lazyParams.rows = event.rows;
            this.loadData();
        },
        onSort(event) {
            this.lazyParams.sortField = event.sortField;
            this.lazyParams.sortOrder = event.sortOrder;
            this.loadData();
        },
        onFilter(event) {
            this.lazyParams.filters = event.filters;
            this.loadData();
        },
        onSearch() {
            console.log("[Clients][onSearch] Searching with:", this.filters['global'].value);
            this.lazyParams.first = 0;
            this.loadData();
        },
        onToggleStandardFilters() {
            console.log("[Clients][onToggleStandardFilters] Applying Standard Filter:", this.selectedStandardFilter);
            this.lazyParams.first = 0;
            this.loadData();
        },
        // onToggleStandardFilters(value) {
        //     if (value.length > 1) {
        //         this.selectedStandardFilter = [value[value.length - 1]]; // ✅ Keep only the last selection
        //     } else {
        //         this.selectedStandardFilter = value;
        //     }
        //
        //     console.log("🔎 Applying Standard Filter:", this.selectedStandardFilter);
        //     this.lazyParams.first = 0; // ✅ Reset pagination on filter change
        //     this.loadData(); // ✅ Fetch filtered data from API
        // },
        clearFilter() {
            this.initFilters();
            console.log("🔄 Clearing search filters...");
            // this.filters['global'] = { value: '' }; // Reset global search
            this.lazyParams.filters = {}; // Clear all column filters
            this.lazyParams.first = 0; // Reset pagination to first page
            this.loadData();
        },
        restoreToOriginal() {
            console.log("🔄 Restoring table to original view...");
            this.lazyParams = {
                first: 0,
                rows: 10,
                sortField: null,
                sortOrder: null,
                filters: { ...this.originalFilters }, // Restore saved filters
            };
            // this.clients = [...this.originalClients]; // Restore original data
            this.loadData();
            window.location.reload();
        },
        callNewCompany() {
            this.newCompany = {};
            this.newCompany.productRole = 'P';
            this.newCompany.type = 'S';
            this.newCompany.addressCountry = 'ZA';
            this.newCompany.assistant = 'N';
            this.newCompany.taxRegistered = 'N';
            this.newCompany.status = 'A';
            this.activeTab = 2;
        },
        async createCompany() {

            this.submitted = true;

            if (this.newCompany.type.trim()) {

                try {

                    console.log("[ClientsNew][saveNewClient] this.newCompany = [", this.newCompany);

                    var newCompanyDto = {
                        productRole: this.newCompany.productRole,
                        type: this.newCompany.type,
                        assistant: this.newCompany.assistant,
                        name: this.newCompany.name,
                        tradingName: this.newCompany.tradingName,
                        registrationNumber: this.newCompany.registrationNumber,
                        firstName: this.newCompany.firstName,
                        lastName: this.newCompany.lastName,
                        userPassword: this.newCompany.userPassword,
                        email: this.newCompany.email,
                        phone: this.newCompany.phone,
                        supportEmail: this.newCompany.supportEmail,
                        supportPhone: this.newCompany.supportPhone,
                        address1: this.newCompany.address1,
                        address2: this.newCompany.address2,
                        addressRegion: this.newCompany.addressRegion,
                        addressState: this.newCompany.addressState,
                        addressCountry: this.newCompany.addressCountry,
                        addressPostalCode: this.newCompany.addressPostalCode,
                        taxRegistered: this.newCompany.taxRegistered,
                        taxNumber: this.newCompany.taxNumber,
                        status: 'A'
                    };

                    console.log("[ClientsNew][saveNewClient] Before creating client back-end call. Data = ", newCompanyDto);

                    const response = await CompanyService.createCompany(newCompanyDto);
                    console.log('[ClientsNew][saveNewClient] Response = ', response);

                    this.loadData();

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Created company',
                        life: 3000
                    });

                } catch (error) {
                    console.error('[ClientsNew][saveNewClient] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to create company. Please try again.',
                        life: 3000
                    });
                }
            }

            this.newCompany = {};
            this.activeTab = 1;
            this.submitted = false;

        },
        // processClientMenu() {
        //     this.$router.push("commonClients");
        // },
        // processAppointmentMenu() {
        //     this.$router.push("commonAppointments");
        // },
        // processPaymentsMenu() {
        //     this.$router.push("commonFinancialPayments");
        // },
        // processInvoicesMenu() {
        //     this.$router.push("commonFinancialInvoices");
        // },
        // processClaimsMenu() {
        //     this.$router.push("healthFinancialClaims");
        // }
    }
};
</script>

<style scoped>
a {
    text-decoration: none;
}
</style>
